import type { UnifiedBranchesData } from '../../lib/branchTypes'
import {
  findDocBoundaries,
  parsePlanASections,
  splitEpilogues,
  handleBranchpointsAndSplits,
  parsePlanEndings,
} from './splitMarkdownToBranches/parsing'
import {
  generateChunkFiles,
  assembleUnifiedJson,
} from './splitMarkdownToBranches/assemble'

export type SplitMarkdownToBranchesResult = {
  unifiedJson: UnifiedBranchesData
  // Map from full relative path (e.g. "generated/branching-markdown-v7/the-playbook-root.md")
  // to the file's content. Used by the orchestrator to write files, and by
  // the editor's in-memory resolver to render without ever touching disk.
  chunkContents: Record<string, string>
  warnings: string[]
}

// Pure split: takes the full markdown + footnote-defs string + the
// existing unified-branches.json (so we can merge hand-edited fields),
// returns the new unifiedJson plus an in-memory map of chunk path → content.
// No fs writes. The caller (orchestrator script OR editor preview / save
// route) decides what to do with the result.
//
// This reads as the six ordered phases of the import; each phase lives in a
// helper under ./splitMarkdownToBranches/.
export function splitMarkdownToBranches({
  markdown,
  footnotesContent,
  existingUnifiedJson,
}: {
  markdown: string
  footnotesContent: string
  existingUnifiedJson: UnifiedBranchesData
}): SplitMarkdownToBranchesResult {
  const warnings: string[] = []
  // Deep-clone so we never mutate the caller's input.
  const unifiedJson: UnifiedBranchesData = structuredClone(existingUnifiedJson)

  // Phase 1: find the root / 2026-2028 / 2029-intro / plan-endings boundaries.
  const boundaries = findDocBoundaries(markdown, warnings)

  // Phase 2: slice Plan A into sections, then split out epilogue sections.
  const planASections = splitEpilogues(parsePlanASections(boundaries.afterIntroSection))

  // Phase 3: parse branchpoints and split sections at section-splitting ones.
  const { sections, branchpoints } = handleBranchpointsAndSplits(planASections, warnings)

  // Phase 4: parse the "What happens in Plan S/B/C/D" ending blocks.
  const planEndings = parsePlanEndings(boundaries.planEndingsContent)

  // Phase 5: generate every per-chunk markdown file.
  const chunks = generateChunkFiles(
    boundaries.rootContent,
    boundaries.section2026_2028,
    sections,
    branchpoints,
    planEndings,
    footnotesContent,
    boundaries.hoistedSectionTitle,
    boundaries.section2026Tail,
  )

  // Phase 6: assemble the unified-branches.json from the parsed pieces.
  assembleUnifiedJson(unifiedJson, chunks, branchpoints, planEndings, footnotesContent)

  return { unifiedJson, chunkContents: chunks.chunkContents, warnings }
}
